import { motion } from 'motion/react';

import type { Finding, Severity } from '@smartapply/shared';

import { HighlightExcerpt } from './HighlightExcerpt.js';
import { MarginNote } from './MarginNote.js';
import { RewriteBlock } from './RewriteBlock.js';
import { ItemId } from '../ui/ItemId.js';

interface FindingCardProps {
  finding: Finding;
  index: number;
  /** Override do prefixo (default: `F`). Para sugestões usamos `S`. */
  severity?: Severity;
}

export function FindingCard({ finding, index, severity }: FindingCardProps) {
  const sectionsLabel = finding.systemsAffected.map(labelOfSystem).join(' · ');
  const prefix = severity === 'suggestion' ? 'S' : 'F';
  const sevColor = colorOfSeverity(finding.severity);
  const sevTint = tintOfSeverity(finding.severity);

  return (
    <motion.article
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25, delay: Math.min(index * 0.04, 0.4), ease: 'easeOut' }}
      className="card-paper relative mb-4 grid gap-6 overflow-hidden rounded-sm py-7 pl-7 pr-6 lg:grid-cols-[1fr_280px] lg:gap-12"
      style={{ borderLeft: `3px solid ${sevColor}` }}
    >
      {/* Banda de tint suave no topo da ficha — reforça severity sem ser badge */}
      <div
        aria-hidden
        className="absolute inset-x-0 top-0 h-1"
        style={{ backgroundColor: sevTint }}
      />
      <div>
        <header className="flex items-baseline gap-3">
          <span
            className="inline-flex items-center rounded-sm px-1.5 py-0.5 font-mono text-[11px] font-semibold uppercase tracking-wider tabular-nums"
            style={{
              color: sevColor,
              backgroundColor: sevTint,
              border: `1px solid ${sevColor}33`,
            }}
            aria-hidden
          >
            {prefix}.{index.toString().padStart(2, '0')}
          </span>
          <span className="sr-only">
            <ItemId prefix={prefix} index={index} severity={finding.severity} />
          </span>
          <h3 className="font-serif text-xl font-medium leading-snug text-[var(--ink-primary)]">
            {finding.title}
          </h3>
        </header>

        <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--ink-tertiary)]">
          afeta: {sectionsLabel}
        </p>

        {finding.citedExcerpt && (
          <div className="mt-5 border-l-2 border-[var(--border-default)] py-2 pl-4">
            <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--ink-tertiary)]">
              trecho atual no perfil
            </p>
            <p className="mt-2 leading-relaxed">
              <HighlightExcerpt text={finding.citedExcerpt} severity={finding.severity} />
            </p>
          </div>
        )}

        {finding.proposedFix && (
          <div className="mt-5">
            <RewriteBlock title="Texto sugerido" text={finding.proposedFix} rationale={null} />
          </div>
        )}

        {finding.proposedFixOptions.length > 0 && (
          <div className="mt-5 space-y-3">
            {finding.proposedFixOptions.map((opt, i) => (
              <RewriteBlock
                key={i}
                title={`Variação ${String.fromCharCode(65 + i)} — ${opt.label}`}
                text={opt.text}
                rationale={opt.rationale}
              />
            ))}
          </div>
        )}

        {finding.estimatedTimeMinutes !== null && (
          <p className="mt-4 font-mono text-[11px] text-[var(--ink-tertiary)]">
            tempo estimado de fix:{' '}
            <span className="tabular-nums text-[var(--ink-secondary)]">
              ~{finding.estimatedTimeMinutes} min
            </span>
          </p>
        )}
      </div>

      <aside className="lg:pt-1">
        <MarginNote severity={finding.severity} label="análise técnica">
          {finding.technicalReason}
        </MarginNote>
      </aside>
    </motion.article>
  );
}

function labelOfSystem(s: Finding['systemsAffected'][number]): string {
  return (
    ({
      recruiter_search: 'Recruiter Search',
      feed_algorithm: 'algoritmo do feed',
      ats: 'ATS',
      first_impression: 'primeira impressão',
    } as const)[s] ?? s
  );
}

function colorOfSeverity(s: Severity): string {
  switch (s) {
    case 'critical':
      return 'var(--accent-crimson)';
    case 'suggestion':
      return 'var(--accent-saffron)';
    case 'positive':
      return 'var(--accent-forest)';
  }
}

function tintOfSeverity(s: Severity): string {
  switch (s) {
    case 'critical':
      return 'var(--accent-crimson-bg)';
    case 'suggestion':
      return 'var(--accent-saffron-bg)';
    case 'positive':
      return 'var(--accent-forest-bg)';
  }
}
